/*******************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2020, 2023. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 *******************************************************************************/

"use strict";

var constants = require("../shared/constants");

// ----------------------------------------------------------------------------

var KP_SERVICE_ID = "keyprotect";
var HV_SERVICE_ID = "hashicorpvault";
var SM_SERVICE_ID = "secretsmanager";
var EN_SERVICE_ID = "eventnotifications";
var KP_REFERENCE_TYPE = "kp";
var HV_REFERENCE_TYPE = "hv";
var SM_REFERENCE_TYPE = "sm";
var VTI_REFERENCE_TYPE = "vault";
var ANY_REFERENCE_TYPE = "any";

// ----------------------------------------------------------------------------

function getVaultIntegrations(templateServices) {
  var servicesArray = templateServices ? Object.values(templateServices) : [];
  return getVaultIntegrationsFromArray(servicesArray);
}

// ----------------------------------------------------------------------------

function getVaultIntegrationsFromArray(servicesArray) {
  var vaultIntegrations = [];
  if (servicesArray) {
    for (var i = 0; i < servicesArray.length; i++) {
      var service = servicesArray[i];
      if ([KP_SERVICE_ID, SM_SERVICE_ID].includes(service.service_id)) {
        if (service.parameters && service.parameters['name']) {
          var isInstanceIdTypeCrn = service.service_id === SM_SERVICE_ID && service.parameters['instance-id-type'] === 'instance-crn';
          if (!isInstanceIdTypeCrn) {
            if (service.parameters['region'] && service.parameters['resource-group'] && service.parameters['instance-name']) {
              var type = service.service_id === KP_SERVICE_ID ? KP_REFERENCE_TYPE : SM_REFERENCE_TYPE;
              vaultIntegrations.push({
                name: service.parameters['name'],
                region: service.parameters['region'],
                resourceGroup: service.parameters['resource-group'],
                instanceName: service.parameters['instance-name'],
                type: type
              });
            }
          } else {
            // isInstanceIdTypeCrn
            if (service.parameters['instance-crn']) {
              vaultIntegrations.push({
                name: service.parameters['name'],
                instanceIdType: service.parameters['instance-id-type'],
                // is "instance-crn"
                instanceCrn: service.parameters['instance-crn'],
                type: SM_REFERENCE_TYPE
              });
            }
          }
        }
      } else if ([HV_SERVICE_ID].includes(service.service_id)) {
        if (service.parameters && service.parameters['name'] && service.parameters['server_url'] && service.parameters['dashboard_url'] && service.parameters['authentication_method']) {
          if (service.parameters['authentication_method'] === "approle" && service.parameters['role_id'] && service.parameters['secret_id']) {
            vaultIntegrations.push({
              name: service.parameters['name'],
              server_url: service.parameters['server_url'],
              dashboard_url: service.parameters['dashboard_url'],
              path: service.parameters['path'],
              secret_filter: service.parameters['secret_filter'],
              default_secret: service.parameters['default_secret'],
              ui_capabilities: service.parameters['ui_capabilities'],
              engine: service.parameters['engine'],
              authentication_method: service.parameters['authentication_method'],
              role_id: service.parameters['role_id'],
              secret_id: service.parameters['secret_id'],
              type: HV_REFERENCE_TYPE
            });
          } else if (service.parameters['authentication_method'] === "token" && service.parameters['token']) {
            vaultIntegrations.push({
              name: service.parameters['name'],
              server_url: service.parameters['server_url'],
              dashboard_url: service.parameters['dashboard_url'],
              path: service.parameters['path'],
              secret_filter: service.parameters['secret_filter'],
              default_secret: service.parameters['default_secret'],
              ui_capabilities: service.parameters['ui_capabilities'],
              engine: service.parameters['engine'],
              authentication_method: service.parameters['authentication_method'],
              token: service.parameters['token'],
              type: HV_REFERENCE_TYPE
            });
          } else if (service.parameters['authentication_method'] === "github" && service.parameters['token']) {
            vaultIntegrations.push({
              name: service.parameters['name'],
              server_url: service.parameters['server_url'],
              dashboard_url: service.parameters['dashboard_url'],
              path: service.parameters['path'],
              secret_filter: service.parameters['secret_filter'],
              default_secret: service.parameters['default_secret'],
              ui_capabilities: service.parameters['ui_capabilities'],
              engine: service.parameters['engine'],
              authentication_method: service.parameters['authentication_method'],
              token: service.parameters['token'],
              type: HV_REFERENCE_TYPE
            });
          } else if (service.parameters['authentication_method'] === "userpass" && service.parameters['username'] && service.parameters['password']) {
            vaultIntegrations.push({
              name: service.parameters['name'],
              server_url: service.parameters['server_url'],
              dashboard_url: service.parameters['dashboard_url'],
              path: service.parameters['path'],
              secret_filter: service.parameters['secret_filter'],
              default_secret: service.parameters['default_secret'],
              ui_capabilities: service.parameters['ui_capabilities'],
              engine: service.parameters['engine'],
              authentication_method: service.parameters['authentication_method'],
              username: service.parameters['username'],
              password: service.parameters['password'],
              type: HV_REFERENCE_TYPE
            });
          }
        }
      }
    }
  }
  return vaultIntegrations;
}

// ----------------------------------------------------------------------------

function hasS2SIntegrations(templateServices) {
  for (var key in templateServices) {
    if (templateServices.hasOwnProperty(key)) {
      var service = templateServices[key];
      if ([KP_SERVICE_ID, SM_SERVICE_ID, EN_SERVICE_ID, "appconfig"].includes(service.service_id)) {
        return true;
      }
    }
  }
  return false;
}

// ----------------------------------------------------------------------------

function hasHashicorpVaultIntegrations(templateServices) {
  for (var key in templateServices) {
    if (templateServices.hasOwnProperty(key)) {
      var service = templateServices[key];
      if ([HV_SERVICE_ID].includes(service.service_id)) {
        return true;
      }
    }
  }
  return false;
}

// ----------------------------------------------------------------------------

function isVaultProperty(propName, capability, context) {
  // Check the feature flag first
  if (!(context && context.enableSecretPicker)) {
    return false;
  }

  // Check params
  if (!(propName && capability)) {
    return false;
  }

  // Check for git access_token
  if (propName === 'access_token') {
    var gitProviders = constants.gitServiceIds;
    if (gitProviders.includes(capability.capabilityId)) {
      // Git broker validators don't support secret references
      return false;
    }
  }
  var paramDef = capability && capability.schema && capability.schema.properties && capability.schema.properties[propName];
  if (paramDef && paramDef.validator && ['api-key', 'service-id-api-key'].includes(paramDef.validator['v-type'])) {
    // Validator-v1 api-key validator supports secret references
    return true;
  }
  if (paramDef && paramDef.type === "password") {
    if (['api-key', 'iam-api-key', 'ibmcloud-api-key'].includes(propName)) {
      // bm-helper validators don't support secret references
      return false;
    }

    // Broker validators are proxied through otc-ui which supports references
    return true;
  }
  return false;
}

// ----------------------------------------------------------------------------

module.exports = {
  getVaultIntegrations: getVaultIntegrations,
  getVaultIntegrationsFromArray: getVaultIntegrationsFromArray,
  hasS2SIntegrations: hasS2SIntegrations,
  hasHashicorpVaultIntegrations: hasHashicorpVaultIntegrations,
  isVaultProperty: isVaultProperty,
  KP_SERVICE_ID: KP_SERVICE_ID,
  HV_SERVICE_ID: HV_SERVICE_ID,
  SM_SERVICE_ID: SM_SERVICE_ID,
  EN_SERVICE_ID: EN_SERVICE_ID,
  KP_REFERENCE_TYPE: KP_REFERENCE_TYPE,
  HV_REFERENCE_TYPE: HV_REFERENCE_TYPE,
  SM_REFERENCE_TYPE: SM_REFERENCE_TYPE,
  VTI_REFERENCE_TYPE: VTI_REFERENCE_TYPE,
  ANY_REFERENCE_TYPE: ANY_REFERENCE_TYPE
};

// ----------------------------------------------------------------------------